<script>
import { mapGetters } from 'vuex';
import LoadingState from './components/widgets/LoadingState.vue';
import NetworkNotification from './components/NetworkNotification.vue';
import UpdateBanner from './components/app/UpdateBanner.vue';
import DeployUpdateBanner from './components/app/DeployUpdateBanner.vue';
import StatusBanner from './components/app/StatusBanner.vue';
import PaymentPendingBanner from './components/app/PaymentPendingBanner.vue';
import PendingEmailVerificationBanner from './components/app/PendingEmailVerificationBanner.vue';
import CallDispositionModal from './routes/dashboard/voipCalls/components/CallDispositionModal.vue';
import vueActionCable from './helper/actionCable';
import { useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import WootSnackbarBox from './components/SnackbarContainer.vue';
import InboxConnectionHealthNotice from './components/app/InboxConnectionHealthNotice.vue';
import { setColorTheme } from './helper/themeHelper';
import { isOnOnboardingView } from 'v3/helpers/RouteHelper';
import { useAccount } from 'dashboard/composables/useAccount';
import { useFontSize } from 'dashboard/composables/useFontSize';
import {
  registerSubscription,
  verifyServiceWorkerExistence,
  hasPushPermissions,
} from './helper/pushHelper';
import ReconnectService from 'dashboard/helper/ReconnectService';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

export default {
  name: 'App',

  components: {
    LoadingState,
    NetworkNotification,
    UpdateBanner,
    DeployUpdateBanner,
    StatusBanner,
    PaymentPendingBanner,
    WootSnackbarBox,
    InboxConnectionHealthNotice,
    PendingEmailVerificationBanner,
    CallDispositionModal,
  },
  setup() {
    const router = useRouter();
    const store = useStore();
    const { accountId } = useAccount();
    // Use the font size composable (it automatically sets up the watcher)
    const { currentFontSize } = useFontSize();
    const { uiSettings } = useUISettings();

    return {
      router,
      store,
      currentAccountId: accountId,
      currentFontSize,
      uiSettings,
    };
  },
  data() {
    return {
      latestChatwootVersion: null,
      reconnectService: null,
    };
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      isRTL: 'accounts/isRTL',
      currentUser: 'getCurrentUser',
      authUIFlags: 'getAuthUIFlags',
      globalConfig: 'globalConfig/get',
    }),
    hideOnOnboardingView() {
      return !isOnOnboardingView(this.$route);
    },
    // SiteUp: título da aba do navegador = "<Nome da Conta do cliente> - <Marca>"
    // (ex.: "Lia Imobiliária - SiteUP"). Ajuda a identificar de qual cliente é
    // cada aba/janela aberta. Sem conta (login/onboarding) mostra só a marca.
    documentTitle() {
      const brand = this.globalConfig?.installationName || 'Support';
      const accountName = this.getAccount(this.currentAccountId)?.name;
      return accountName ? `${accountName} - ${brand}` : brand;
    },
    // Primitive key so the listener retries when features or identity
    // arrive after initializeAccount, without looping on object identity.
    voipListenerSyncKey() {
      const account = this.getAccount(this.currentAccountId) || {};
      const hasFeatures = Object.prototype.hasOwnProperty.call(
        account,
        'features'
      );
      const user = this.currentUser || {};
      return [
        this.currentAccountId || '',
        user.id || '',
        hasFeatures,
        !!account.features?.voip_calls,
        user.pubsub_token || '',
      ].join('|');
    },
    voipIdentityComplete() {
      const account = this.getAccount(this.currentAccountId) || {};
      const user = this.currentUser || {};
      return !!(
        account.features?.voip_calls &&
        this.currentAccountId &&
        user.id &&
        user.pubsub_token
      );
    },
  },

  watch: {
    currentAccountId: {
      immediate: true,
      handler() {
        if (this.currentAccountId) {
          this.initializeAccount();
        }
      },
    },
    // Só RECUPERA um listener ausente; nunca derruba um ativo. O caminho
    // fail-closed do sync chama stopStackZapMedia(), que corta o áudio de uma
    // ligação em curso, e uma resposta parcial da conta no meio da sessão faria
    // essa chave regredir. Derrubar por troca de conta continua no watcher de
    // currentAccountId, que é onde isso é intencional.
    voipListenerSyncKey() {
      if (this.currentAccountId && this.voipIdentityComplete) {
        this.syncVoipStackZapForAccount();
      }
    },
    // Mantém o título da aba sincronizado com a conta atual (troca de conta,
    // carregamento inicial ou renomeação refletem na hora).
    documentTitle: {
      immediate: true,
      handler(title) {
        if (title) {
          document.title = title;
        }
      },
    },
  },
  mounted() {
    this.initializeColorTheme();
    this.listenToThemeChanges();
    // If user locale is set, use it; otherwise use account locale
    this.setLocale(
      this.uiSettings?.locale || window.chatwootConfig.selectedLocale
    );
    emitter.on(BUS_EVENTS.WEBSOCKET_RECONNECT, this.onWebsocketReconnect);
  },
  async unmounted() {
    emitter.off(BUS_EVENTS.WEBSOCKET_RECONNECT, this.onWebsocketReconnect);
    if (this.reconnectService) {
      this.reconnectService.disconnect();
    }
    try {
      const { stopStackZapCallListener } = await import(
        'dashboard/composables/useStackZapCallSession'
      );
      stopStackZapCallListener();
    } catch {
      /* ignore */
    }
  },
  methods: {
    initializeColorTheme() {
      setColorTheme(window.matchMedia('(prefers-color-scheme: dark)').matches);
    },
    listenToThemeChanges() {
      const mql = window.matchMedia('(prefers-color-scheme: dark)');
      mql.onchange = e => setColorTheme(e.matches);
    },
    setLocale(locale) {
      if (locale) {
        this.$root.$i18n.locale = locale;
      }
    },
    onWebsocketReconnect() {
      // Mesma regra do watcher: reconectar só reinstala, nunca derruba.
      if (this.currentAccountId && this.voipIdentityComplete) {
        this.syncVoipStackZapForAccount();
      }
    },
    // Real App method invoked from initializeAccount (and unit-tested directly).
    // Every account context either starts the current listener or fully stops prior.
    async syncVoipStackZapForAccount() {
      const { syncStackZapCallListenerForAccount } = await import(
        'dashboard/composables/useStackZapCallSession'
      );
      const user = this.currentUser || {};
      const featureEnabled = !!this.getAccount(this.currentAccountId)?.features
        ?.voip_calls;
      syncStackZapCallListenerForAccount({
        accountId: this.currentAccountId,
        userId: user.id,
        pubsubToken: user.pubsub_token,
        store: this.store,
        featureEnabled,
      });
    },
    async initializeAccount() {
      await this.$store.dispatch('accounts/get');
      this.$store.dispatch('setActiveAccount', {
        accountId: this.currentAccountId,
      });
      const account = this.getAccount(this.currentAccountId);
      const { locale, latest_chatwoot_version: latestChatwootVersion } =
        account;
      const { pubsub_token: pubsubToken } = this.currentUser || {};
      // If user locale is set, use it; otherwise use account locale
      this.setLocale(this.uiSettings?.locale || locale);
      this.latestChatwootVersion = latestChatwootVersion;
      vueActionCable.init(this.store, pubsubToken);

      // StackZap call surface (flag rename deferred to CLEANUP-1).
      // No VoIP webphone boot. Fail-closed on feature-off / incomplete identity.
      await this.syncVoipStackZapForAccount();

      this.reconnectService = new ReconnectService(this.store, this.router);
      window.reconnectService = this.reconnectService;

      // Auto-registra o push quando a permissao do navegador ja foi concedida.
      // Antes, so re-sincronizava uma subscription EXISTENTE — entao quem
      // concedeu permissao mas perdeu/nunca criou a subscription ficava sem
      // push em background silenciosamente. registerSubscription e idempotente:
      // pushManager.subscribe retorna a subscription existente ou cria uma nova.
      verifyServiceWorkerExistence(() => {
        if (hasPushPermissions()) {
          registerSubscription();
        }
      });
    },
  },
};
</script>

<template>
  <!--
    Sem viewport-fit=cover o iOS mantem o PWA dentro da area segura e cuida
    sozinho do entalhe e da barra de gestos; env(safe-area-inset-*) vale 0.
    Nenhum recuo de safe-area aqui: padding no #app empilhava faixa morta
    entre a lista e a nav (#589).
  -->
  <div
    v-if="!authUIFlags.isFetching"
    id="app"
    class="flex flex-col w-full h-full min-h-0 bg-n-background"
    :dir="isRTL ? 'rtl' : 'ltr'"
  >
    <UpdateBanner :latest-chatwoot-version="latestChatwootVersion" />
    <DeployUpdateBanner />
    <StatusBanner />
    <template v-if="currentAccountId">
      <PendingEmailVerificationBanner v-if="hideOnOnboardingView" />
      <PaymentPendingBanner v-if="hideOnOnboardingView" />
    </template>
    <router-view v-slot="{ Component }">
      <transition name="fade" mode="out-in">
        <component :is="Component" />
      </transition>
    </router-view>
    <WootSnackbarBox />
    <InboxConnectionHealthNotice v-if="currentAccountId" />
    <NetworkNotification />
    <CallDispositionModal />
  </div>
  <LoadingState v-else />
</template>

<style lang="scss">
@import './assets/scss/app';

.v-popper--theme-tooltip .v-popper__inner {
  background: black !important;
  font-size: 0.75rem;
  padding: 4px 8px !important;
  @apply rounded-[var(--workspace-radius,6px)];
  font-weight: 400;
}

.v-popper--theme-tooltip .v-popper__arrow-container {
  display: none;
}
</style>
